import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { UserProfile, V2AuthContext } from '@shared/types/auth';

const AUTH_STORE_KEY = 'jema_auth_store_v2';
const AUTH_STORE_VERSION = 2;
const AUTH_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

type AuthStoreState = {
  user: UserProfile | null;
  authContext: V2AuthContext | null;
  capabilities: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
  persistedAt: number | null;
  setAuthSnapshot: (payload: {
    user: UserProfile | null;
    authContext: V2AuthContext | null;
    isAuthenticated: boolean;
    isLoading: boolean;
  }) => void;
  setLoading: (isLoading: boolean) => void;
  clearAuth: () => void;
  validateSnapshot: () => void;
};

const baseState = {
  user: null,
  authContext: null,
  capabilities: [],
  isAuthenticated: false,
  isLoading: true,
  persistedAt: null,
};

const isSnapshotExpired = (persistedAt: number | null) => {
  if (!persistedAt) return true;
  return Date.now() - persistedAt > AUTH_SNAPSHOT_TTL_MS;
};

export const useAuthStore = create<AuthStoreState>()(
  persist(
    (set, get) => {
      return {
        ...baseState,

        setAuthSnapshot: ({ user, authContext, isAuthenticated, isLoading }) => {
          set({
            user,
            authContext,
            capabilities: authContext?.capabilities ?? [],
            isAuthenticated,
            isLoading,
            persistedAt: Date.now(),
          });
        },

        setLoading: (isLoading) => {
          set((state) => ({
            ...state,
            isLoading,
          }));
        },

        clearAuth: () => {
          set({
            ...baseState,
            isLoading: false,
          });
        },

        validateSnapshot: () => {
          const { persistedAt } = get();
          if (!isSnapshotExpired(persistedAt)) return;

          set({
            ...baseState,
            isLoading: true,
          });
        },
      };
    },
    {
      name: AUTH_STORE_KEY,
      version: AUTH_STORE_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        authContext: state.authContext,
        capabilities: state.capabilities,
        isAuthenticated: state.isAuthenticated,
        isLoading: state.isLoading,
        persistedAt: state.persistedAt,
      }),
      migrate: (persistedState, version) => {
        if (version !== AUTH_STORE_VERSION) {
          return {
            ...baseState,
          };
        }

        return persistedState as Omit<AuthStoreState, 'setAuthSnapshot' | 'setLoading' | 'clearAuth' | 'validateSnapshot'>;
      },
      onRehydrateStorage: () => (state) => {
        state?.validateSnapshot();
      },
    }
  )
);

export const useAuthUser = () => useAuthStore((state) => state.user);
export const useAuthContextSnapshot = () => useAuthStore((state) => state.authContext);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
